import { useMemo, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { Institution } from "../Models/Institution";

interface IProps {
  institutions: Institution[];
  selectedInstitutionIds: number[];
  onChange: (institutionIds: number[]) => void;
  disabled?: boolean;
}

export default function InstitutionFilter(props: IProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const selectedInstitutions = useMemo(
    () => props.institutions.filter(_ => props.selectedInstitutionIds.includes(_.id)),
    [props.institutions, props.selectedInstitutionIds]
  );
  const filteredInstitutions = useMemo(
    () => normalizedSearchTerm
      ? props.institutions.filter(_ => _.name.toLocaleLowerCase().includes(normalizedSearchTerm))
      : props.institutions,
    [props.institutions, normalizedSearchTerm]
  );

  const toggleInstitution = (institutionId: number, checked: boolean) => {
    const nextIds = checked
      ? [...props.selectedInstitutionIds, institutionId]
      : props.selectedInstitutionIds.filter(_ => _ !== institutionId);

    props.onChange(Array.from(new Set(nextIds)));
  };

  const selectionSummary = selectedInstitutions.length === 0
    ? "All institutions"
    : selectedInstitutions.length === 1
      ? selectedInstitutions[0].name
      : `${selectedInstitutions.length} institutions selected`;

  return (
    <Dropdown
      className="aa-institution-filter"
      autoClose="outside"
      onToggle={isOpen => { if (!isOpen) setSearchTerm(""); }}
    >
      <Dropdown.Toggle
        id="InstitutionFilter"
        className="aa-institution-filter__toggle"
        variant="outline-secondary"
        disabled={props.disabled}
      >
        <span className="text-truncate">{selectionSummary}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="aa-institution-filter__menu">
        <div className="px-3 pt-2 pb-1">
          <Form.Control
            type="search"
            size="sm"
            autoFocus
            aria-label="Search institutions"
            placeholder="Type to search"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="aa-institution-filter__options" role="group" aria-label="Institutions">
          {filteredInstitutions.map(institution =>
            <Form.Check
              key={institution.id}
              id={`InstitutionIds-${institution.id}`}
              type="checkbox"
              label={institution.name}
              checked={props.selectedInstitutionIds.includes(institution.id)}
              onChange={event => toggleInstitution(institution.id, event.target.checked)}
            />
          )}
          {filteredInstitutions.length === 0 &&
            <p className="text-muted mb-0 px-1 py-2">No institutions match your search.</p>
          }
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
